import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isMobile, setIsMobile] = useState(false);
  const [isPpcHovered] = useState(false);
  const { t } = useTranslation();
  const isIpad = useMediaQuery("(max-width:1200px)");

  const userId = Cookies.get("id");

  // Check if screen width is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Clean up
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const styles = {
    footer: {
      width: "100%",
      backgroundColor: "#020d0e",
      padding: isMobile ? "30px" : "60px",
      overflow: "hidden",
    },
    logo: {
      fontSize: isMobile ? "32px" : "40px",
      fontWeight: "bold",
      color: "#ffb60c",
    },
    ppc: {
      fontSize: isMobile ? "20px" : "30px",
      fontWeight: "bold",
      color: "white",
      cursor: "pointer",
    },
    ppcLink: {
      color: "inherit",
      textDecoration: "none",
      transition: "font-size 0.2s ease, color 0.2s ease",
      fontSize: isPpcHovered ? (isMobile ? "18px" : "24px") : "inherit",
    },
    menuRow: {
      display: "flex",
      flexDirection: isIpad ? "column" : "row",
      alignItems: isIpad ? "center" : "flex-start",
      justifyContent: isIpad ? "center" : "center",
      width: "100%",
      maxWidth: "1400px",
      margin: isIpad ? "20px auto 0 auto" : "32px auto 0 auto",
      position: "relative",
      gap: isIpad ? "16px" : "0",
    },
    menuItems: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      flex: 1,
      gap: isIpad ? "25px" : "48px",
      fontSize: isIpad ? "15px" : "17px",
      fontWeight: 400,
      color: "white",
      flexWrap: isIpad ? "wrap" : "nowrap",
      textAlign: "center",
      width: isIpad ? "85%" : "auto",
    },
    menuItem: {
      color: "white",
      textDecoration: "none",
      padding: 0,
      fontSize: isMobile ? "15px" : "17px",
      fontWeight: 400,
      whiteSpace: "nowrap",
      transition: "color 0.2s",
    },
    copyright: {
      margin: "32px 0 0 0",
      color: "white",
      fontSize: "14px",
      lineHeight: "1.4",
      textAlign: "center",
      width: "100%",
    },
    socialIcons: {
      display: "flex",
      gap: "24px",
      alignItems: "center",
      justifyContent: isIpad ? "center" : "flex-end",
      position: isIpad ? "static" : "absolute",
      right: isIpad ? "auto" : 0,
      top: isIpad ? "auto" : 0,
      height: isIpad ? "auto" : "100%",
      margin: isIpad ? "24px 0 0 0" : 0,
    },
    socialIcon: {
      width: "24px",
      height: "24px",
      opacity: 0.85,
    },
  };

  return (
    <footer style={styles.footer}>
      {/* Grid Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
        gap: isMobile ? '32px' : '48px',
        maxWidth: '1400px',
      }}>

        {/* Left Column: Social Icons, Divider, Copyright */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <div style={styles.logo}>PTag</div>
          
          {/* Social Icons */}
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
          }}>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ transition: 'opacity 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.querySelector('svg').style.fill = '#ffb60c'}
              onMouseLeave={(e) => e.currentTarget.querySelector('svg').style.fill = 'white'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ transition: 'fill 0.2s' }}>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/petboysclub"
              target="_blank"
              rel="noopener noreferrer"
              style={{ transition: 'opacity 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.querySelector('svg').style.fill = '#ffb60c'}
              onMouseLeave={(e) => e.currentTarget.querySelector('svg').style.fill = 'white'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ transition: 'fill 0.2s' }}>
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href="mailto:info@ptag.com"
              style={{ transition: 'opacity 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.querySelector('svg').style.fill = '#ffb60c'}
              onMouseLeave={(e) => e.currentTarget.querySelector('svg').style.fill = 'white'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ transition: 'fill 0.2s' }}>
                <path d="M0 3v18h24v-18h-24zm6.623 7.929l-4.623 5.712v-9.458l4.623 3.746zm-4.141-5.929h19.035l-9.517 7.713-9.518-7.713zm5.694 7.188l3.824 3.099 3.83-3.104 5.612 6.817h-18.779l5.513-6.812zm9.208-1.264l4.616-3.741v9.348l-4.616-5.607z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Products Column */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          <h3 style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: 'bold',
            margin: 0,
          }}>{t('Products')}</h3>
          <Link to="/ptag" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            PTag
          </Link>
          <Link to="/ptag-air" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            PTag Air
          </Link>
        </div>

        {/* Explore Column */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          <h3 style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: 'bold',
            margin: 0,
          }}>{t('Explore')}</h3>
          <Link to="/landing" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            {t('checkPetTags2')}
          </Link>
          <Link to="/login" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            {t('loginFooter')}
          </Link>
          {userId && (
            <Link to="/pet-management" style={{
              color: 'white',
              textDecoration: 'none',
              fontSize: '16px',
              transition: 'color 0.2s',
            }}
              onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
              onMouseLeave={(e) => e.target.style.color = 'white'}>
              {t('PetManagementFooter')}
            </Link>
          )}
        </div>
      </div>
      {/* Divider */}
      <div style={{
        width: '100%',
        height: '1px',
        margin: '32px 0',
        backgroundColor: 'white',
        opacity: 0.3,
      }}></div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '16px' : '0' }}>
        {/* Copyright */}
        <div style={{
          color: 'white',
          fontSize: '14px',
          lineHeight: '1.6',
        }}>
          Copyright© {currentYear} Pet Pet Club Ltd. {t('AllRightReserved')}
        </div>

        {/* Right Column: About Us and Privacy Policy */}
        <div style={{
          display: 'flex',
          gap: '20px',
        }}>
          <Link to="/aboutus" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            {t('AboutUs')}
          </Link>
          <Link to="/privacy" style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.color = '#ffb60c'}
            onMouseLeave={(e) => e.target.style.color = 'white'}>
            {t('PrivacyPolicyFooter')}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
